import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import PageTabs from 'pages/parts/PageTabs'

/**
 * Сотрудники админки и их доступы.
 *
 * Раньше админ был один и видел всё. Здесь заводятся учётки продажников и операторов
 * поддержки: у каждого свой логин с паролем и свой набор разделов.
 *
 * Права берём тем же деревом, что и у сотрудников заведения (`/helper/permissions`),
 * только в области `admin`. Должность — заготовленный набор галочек: выбрал её —
 * галочки проставились, дальше их можно поправить конкретному человеку.
 */

const emptyAdministrator = {
	login: '',
	first_name: '',
	phone: '',
	password: '',
	permission_group_id: '',
}

const emptyGroup = {
	name: '',
}

function Index() {
	const [activeTab, setActiveTab] = useState('administrators')
	const [data, setData] = useState([])
	const [groups, setGroups] = useState([])

	const [modal, setModal] = useState(false)
	const [groupModal, setGroupModal] = useState(false)
	const [selectedItem, setSelectedItem] = useState(emptyAdministrator)
	const [selectedGroup, setSelectedGroup] = useState(emptyGroup)
	const [sections, setSections] = useState([])

	async function loadData() {
		const response = await GET('/admin/administrators', {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data || [])
		}
	}

	async function loadGroups() {
		const response = await GET('/admin/permission-groups', {})
		if (httpOk(response)) {
			setGroups(response.data || [])
		}
	}

	/**
	 * Дерево прав админки. Секции приходят готовыми, а `checked` — по конкретному
	 * сотруднику, поэтому для новой учётки просто не передаём логин.
	 */
	async function loadPermissions(userLogin = '') {
		var url = '/helper/permissions?scope=admin'
		if (userLogin) {
			url += '&user_login=' + userLogin
		}

		const response = await GET(url, {})
		if (!httpOk(response)) {
			return []
		}

		const category = (response.data || [])[0]
		if (!category) {
			return []
		}

		const result = (category.sections || []).map(section => ({
			id: section.id,
			name: section.name,
			permissions: (section.permissions || []).map(permission => ({ ...permission })),
		}))

		// Права без секции показываем отдельной группой — иначе они молча пропадут.
		if ((category.permissions || []).length > 0) {
			const withoutSection = category.permissions.filter(permission => !permission.section_id)

			if (withoutSection.length > 0) {
				result.push({
					id: 0,
					name: 'Прочее',
					permissions: withoutSection.map(permission => ({ ...permission })),
				})
			}
		}

		return result
	}

	async function toggleModal(bool = false, item = emptyAdministrator) {
		if (!bool) {
			setModal(false)
			return
		}

		const tree = await loadPermissions(item.login || '')

		setSections(tree)
		setSelectedItem({
			...emptyAdministrator,
			...item,
			password: '',
			phone: item.phone || '',
			permission_group_id: item.permission_group_id || '',
		})
		setModal(true)
	}

	async function toggleGroupModal(bool = false, item = emptyGroup) {
		if (!bool) {
			setGroupModal(false)
			return
		}

		const tree = await loadPermissions('')
		const selectedIds = (item.permissions || []).map(permission => permission.id)

		setSections(markChecked(tree, selectedIds))
		setSelectedGroup({ ...emptyGroup, ...item })
		setGroupModal(true)
	}

	function markChecked(tree, ids) {
		return tree.map(section => ({
			...section,
			permissions: section.permissions.map(permission => ({
				...permission,
				checked: ids.indexOf(permission.id) >= 0,
			})),
		}))
	}

	function checkedIds() {
		const ids = []

		for (let i = 0; i < sections.length; i++) {
			for (let j = 0; j < sections[i].permissions.length; j++) {
				if (sections[i].permissions[j].checked) {
					ids.push(sections[i].permissions[j].id)
				}
			}
		}

		return ids
	}

	/**
	 * Раздел и действия внутри него связаны: сняли раздел — снимаются его действия,
	 * поставили действие — включается раздел. Иначе получаются права на «провести
	 * оплату» в разделе, которого человек не видит.
	 */
	function togglePermission(sectionIndex, permissionId) {
		const sectionsCopy = sections.map(section => ({
			...section,
			permissions: section.permissions.map(permission => ({ ...permission })),
		}))

		const permissions = sectionsCopy[sectionIndex].permissions
		const target = permissions.find(permission => permission.id === permissionId)

		if (!target) {
			return
		}

		target.checked = !target.checked

		if (!target.parent_id) {
			for (let i = 0; i < permissions.length; i++) {
				if (permissions[i].parent_id === target.id) {
					permissions[i].checked = target.checked
				}
			}
		}

		if (target.parent_id && target.checked) {
			const parent = permissions.find(permission => permission.id === target.parent_id)
			if (parent) {
				parent.checked = true
			}
		}

		setSections(sectionsCopy)
	}

	function toggleSection(sectionIndex, checked) {
		const sectionsCopy = sections.map(section => ({
			...section,
			permissions: section.permissions.map(permission => ({ ...permission })),
		}))

		sectionsCopy[sectionIndex].permissions = sectionsCopy[sectionIndex].permissions
			.map(permission => ({ ...permission, checked: checked }))

		setSections(sectionsCopy)
	}

	/** Выбор должности проставляет её набор — дальше его можно поправить руками. */
	function applyGroup(groupId) {
		setSelectedItem({ ...selectedItem, permission_group_id: groupId })

		if (!groupId) {
			return
		}

		const group = groups.find(item => String(item.id) === String(groupId))
		if (!group) {
			return
		}

		setSections(markChecked(sections, (group.permissions || []).map(permission => permission.id)))
	}

	async function save(e) {
		e.preventDefault()

		const payload = {
			...selectedItem,
			permission_group_id: selectedItem.permission_group_id || null,
			permissions: checkedIds(),
		}

		if (!payload.password) {
			delete payload.password
		}

		const response = selectedItem.id
			? await PUT('/admin/administrators', payload, { loader: true })
			: await POST('/admin/administrators', payload, { loader: true })

		if (httpOk(response)) {
			setModal(false)
			loadData()
		}
	}

	async function saveGroup(e) {
		e.preventDefault()

		const payload = { ...selectedGroup, permissions: checkedIds() }

		const response = selectedGroup.id
			? await PUT('/admin/permission-groups', payload, { loader: true })
			: await POST('/admin/permission-groups', payload, { loader: true })

		if (httpOk(response)) {
			setGroupModal(false)
			loadGroups()
			loadData()
		}
	}

	async function changeStatus(item) {
		const response = await PUT('/admin/administrators/status', {
			id: item.id,
			status: Number(item.status) === 1 ? -2 : 1,
		}, { loader: true })

		if (httpOk(response)) {
			loadData()
		}
	}

	async function deleteGroup(item) {
		const response = await DELETE('/admin/permission-groups/' + item.id, {}, { loader: true })

		if (httpOk(response)) {
			loadGroups()
		}
	}

	useEffect(() => {
		loadData()
		loadGroups()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center mb-0">Администраторы</h4>
				{activeTab === 'administrators' ?
					<button className="btn btn-primary" onClick={() => toggleModal(true)}>Добавить</button>
					:
					<button className="btn btn-primary" onClick={() => toggleGroupModal(true)}>Добавить должность</button>
				}
			</div>

			<PageTabs
				active={activeTab}
				onChange={setActiveTab}
				tabs={[
					{ key: 'administrators', label: renderTabLabel('Сотрудники', data.length, activeTab === 'administrators') },
					{ key: 'groups', label: renderTabLabel('Должности', groups.length, activeTab === 'groups') },
				]}
			/>

			{activeTab === 'administrators' &&
				<div className="card">
					<div className="card-body">
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ width: '33px' }} className="text-center">#</th>
										<th>Логин</th>
										<th>Имя</th>
										<th>Телефон</th>
										<th>Должность</th>
										<th style={{ width: '100px' }} className="text-center">Разделов</th>
										<th style={{ width: '110px' }} className="text-center">Статус</th>
										<th style={{ width: '70px' }}></th>
									</tr>
								</thead>
								<tbody>
									{data.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td>{item.login}</td>
											<td>{item.first_name}</td>
											<td>{item.phone}</td>
											<td>{item.permission_group_name || <span className="text-muted">-</span>}</td>
											<td className="text-center">{item.permissions_count}</td>
											<td className={'text-center text-white ' + (Number(item.status) === 1 ? 'bg-success' : 'bg-danger')}>
												{Number(item.status) === 1 ? 'Активен' : 'Отключён'}
											</td>
											<td>
												{/* Кнопки прижаты влево: у admin нет «Отключить», и «Изменить» не должна прыгать по строкам */}
												<div className="d-flex gap-2">
													<div className="table-action-button table-action-primary-button" title="Изменить"
														onClick={() => toggleModal(true, item)}>
														<i className="uil uil-edit-alt"></i>
													</div>
													{item.login !== 'admin' &&
														(Number(item.status) === 1 ?
															<div className="table-action-button table-action-danger-button" title="Отключить"
																onClick={() => changeStatus(item)}>
																<i className="uil uil-ban"></i>
															</div>
															:
															<div className="table-action-button table-action-success-button" title="Включить"
																onClick={() => changeStatus(item)}>
																<i className="uil uil-check"></i>
															</div>
														)
													}
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			}

			{activeTab === 'groups' &&
				<div className="card">
					<div className="card-body">
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ width: '33px' }} className="text-center">#</th>
										<th>Должность</th>
										<th style={{ width: '100px' }} className="text-center">Прав</th>
										<th style={{ width: '110px' }} className="text-center">Сотрудников</th>
										<th style={{ width: '70px' }}></th>
									</tr>
								</thead>
								<tbody>
									{groups.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td>{item.name}</td>
											<td className="text-center">{item.permissions_count}</td>
											<td className="text-center">{item.users_count}</td>
											<td>
												<div className="d-flex gap-2">
													<div className="table-action-button table-action-primary-button" title="Изменить"
														onClick={() => toggleGroupModal(true, item)}>
														<i className="uil uil-edit-alt"></i>
													</div>
													<div className="table-action-button table-action-danger-button" title="Удалить"
														onClick={() => deleteGroup(item)}>
														<i className="uil uil-trash-alt"></i>
													</div>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			}

			{/* MODAL */}
			<Modal show={modal} animation={false} centered size="lg" backdrop="static"
				onHide={() => setModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{selectedItem.id ? 'Изменить администратора' : 'Новый администратор'}</Modal.Title>
				</Modal.Header>
				<form onSubmit={save}>
					{/* Высота фиксированная: список прав разрастается на полсотни галочек,
					    и без этого окно тянулось во весь экран, а «Сохранить» уезжало вниз */}
					<Modal.Body style={{ height: 'calc(100vh - 260px)', overflowY: 'auto' }}>
						<div className="row">
							<div className="col-md-6">
								<div className="form-group">
									<label>Логин</label>
									<input type="text" className="form-control" autoComplete="off"
										disabled={!!selectedItem.id}
										value={selectedItem.login}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'login': e.target.value })} />
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Имя</label>
									<input type="text" className="form-control"
										value={selectedItem.first_name}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'first_name': e.target.value })} />
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Телефон</label>
									<input type="text" className="form-control"
										value={selectedItem.phone}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'phone': e.target.value })} />
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Пароль</label>
									<input type="password" className="form-control" autoComplete="new-password"
										placeholder={selectedItem.id ? 'Оставьте пустым, чтобы не менять' : ''}
										value={selectedItem.password}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'password': e.target.value })} />
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Должность</label>
									<select className="form-select"
										value={selectedItem.permission_group_id}
										onChange={(e) => applyGroup(e.target.value)}>
										<option value="">Без должности</option>
										{groups.map((group, index) => (
											<option key={index} value={group.id}>{group.name}</option>
										))}
									</select>
								</div>
							</div>
						</div>

						<hr />

						{renderPermissions(sections, togglePermission, toggleSection)}
					</Modal.Body>
					<Modal.Footer>
						<button type="button" className="btn btn-warning"
							onClick={() => setModal(false)}>Отмена</button>
						<button type="submit" className="btn btn-primary">Сохранить</button>
					</Modal.Footer>
				</form>
			</Modal>

			{/* MODAL GROUP */}
			<Modal show={groupModal} animation={false} centered size="lg" backdrop="static"
				onHide={() => setGroupModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{selectedGroup.id ? 'Изменить должность' : 'Новая должность'}</Modal.Title>
				</Modal.Header>
				<form onSubmit={saveGroup}>
					{/* Тот же список прав, что и у администратора — высота такая же */}
					<Modal.Body style={{ height: 'calc(100vh - 260px)', overflowY: 'auto' }}>
						<div className="form-group">
							<label>Название</label>
							<input type="text" className="form-control"
								value={selectedGroup.name}
								onChange={(e) => setSelectedGroup({ ...selectedGroup, 'name': e.target.value })} />
						</div>

						<hr />

						{renderPermissions(sections, togglePermission, toggleSection)}
					</Modal.Body>
					<Modal.Footer>
						<button type="button" className="btn btn-warning"
							onClick={() => setGroupModal(false)}>Отмена</button>
						<button type="submit" className="btn btn-primary">Сохранить</button>
					</Modal.Footer>
				</form>
			</Modal>
		</>
	)
}

/** Название вкладки со счётчиком: у активной счётчик в цвет вкладки, у остальных — приглушённый. */
function renderTabLabel(title, count, active) {
	return (
		<>
			{title}
			<span className={'badge rounded-pill ms-2 ' + (active ? 'bg-primary' : 'bg-secondary')}>{count}</span>
		</>
	)
}

/**
 * Разделы и действия внутри них. Действие рисуем с отступом под своим разделом:
 * плоский список из полусотни галочек прочитать нельзя.
 */
function renderPermissions(sections, togglePermission, toggleSection) {
	return sections.map((section, sectionIndex) => {
		const parents = section.permissions.filter(permission => !permission.parent_id)
		const allChecked = section.permissions.every(permission => permission.checked)

		return (
			<div className="mb-3" key={sectionIndex}>
				<div className="d-flex justify-content-between align-items-center mb-2">
					<b>{section.name}</b>
					<button type="button" className="btn btn-outline-primary btn-sm"
						onClick={() => toggleSection(sectionIndex, !allChecked)}>
						{allChecked ? 'Снять все' : 'Отметить все'}
					</button>
				</div>

				{parents.map((parent, parentIndex) => {
					const children = section.permissions.filter(permission => permission.parent_id === parent.id)

					return (
						<div className="mb-2" key={parentIndex}>
							<div className="form-check">
								<input className="form-check-input" type="checkbox"
									id={'permission-' + parent.id}
									checked={!!parent.checked}
									onChange={() => togglePermission(sectionIndex, parent.id)} />
								<label className="form-check-label" htmlFor={'permission-' + parent.id}>
									{parent.name}
									{parent.description &&
										<span className="text-muted"> — {parent.description}</span>
									}
								</label>
							</div>

							{children.length > 0 &&
								<div className="ps-4">
									{children.map((child, childIndex) => (
										<div className="form-check" key={childIndex}>
											<input className="form-check-input" type="checkbox"
												id={'permission-' + child.id}
												checked={!!child.checked}
												onChange={() => togglePermission(sectionIndex, child.id)} />
											<label className="form-check-label" htmlFor={'permission-' + child.id}>
												{child.name}
												{child.description &&
													<span className="text-muted"> — {child.description}</span>
												}
											</label>
										</div>
									))}
								</div>
							}
						</div>
					)
				})}
			</div>
		)
	})
}

export default Index
